/**
 * NTAccordion - High Performance, Buttery Smooth Accordion Controller
 * Designed for NewTech Labs custom sections (FAQ, PDP FAQ, Collapsible components)
 * Features:
 * - Silky smooth 60fps Web Animations API (WAAPI)
 * - Interruptible animations (smooth reversal mid-flight)
 * - Coordinated icon rotation and content fade-in/slide-down
 * - Keyboard accessibility (A11y, ARIA attributes)
 * - Single-open accordion group support (data-single-open)
 * - Shopify Theme Editor live reload & block select support
 * - Prefers-reduced-motion compliance
 */
(function () {
  'use strict';

  const DURATION_OPEN = 320;
  const DURATION_CLOSE = 260;
  const EASING_OPEN = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const EASING_CLOSE = 'cubic-bezier(0.25, 1, 0.5, 1)';

  class NTAccordionItem {
    constructor(detailsEl) {
      this.el = detailsEl;
      this.summary = detailsEl.querySelector('summary');
      if (!this.summary) return;

      // Ensure proper structure: summary + .nt-accordion-body > .nt-accordion-content
      this.ensureBodyWrapper();

      this.body = detailsEl.querySelector('.nt-accordion-body');
      this.content = detailsEl.querySelector('.nt-accordion-content');
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;

      // Initial ARIA and expanded state
      const isOpen = this.el.hasAttribute('open');
      this.summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen) {
        this.el.classList.add('is-expanded');
      }

      this.bindEvents();
    }

    ensureBodyWrapper() {
      let body = this.el.querySelector('.nt-accordion-body');
      if (!body) {
        const content = this.el.querySelector('.nt-accordion-content');
        if (content) {
          body = document.createElement('div');
          body.className = 'nt-accordion-body';
          content.parentNode.insertBefore(body, content);
          body.appendChild(content);
        } else {
          // Wrap all non-summary children
          const nonSummary = Array.from(this.el.children).filter(
            (c) => c.tagName.toLowerCase() !== 'summary'
          );
          if (nonSummary.length > 0) {
            body = document.createElement('div');
            body.className = 'nt-accordion-body';
            const wrapper = document.createElement('div');
            wrapper.className = 'nt-accordion-content';
            nonSummary.forEach((child) => wrapper.appendChild(child));
            body.appendChild(wrapper);
            this.el.appendChild(body);
          }
        }
      }
    }

    bindEvents() {
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(e) {
      e.preventDefault();

      // Check prefers-reduced-motion
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        if (this.el.open) {
          this.el.open = false;
          this.el.classList.remove('is-expanded');
          this.summary.setAttribute('aria-expanded', 'false');
        } else {
          this.el.open = true;
          this.el.classList.add('is-expanded');
          this.summary.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      if (this.isClosing || !this.el.open) {
        this.open();
      } else if (this.isExpanding || this.el.open) {
        this.close();
      }
    }

    open() {
      if (this.isExpanding) return;

      // Handle single-open accordion group if parent enables it
      this.handleSingleOpenGroup();

      this.isClosing = false;
      this.isExpanding = true;

      // Make element visible in DOM so dimensions are measurable
      this.el.open = true;
      this.el.classList.add('is-animating', 'is-expanded');
      this.summary.setAttribute('aria-expanded', 'true');

      if (!this.body) {
        this.isExpanding = false;
        return;
      }

      const startHeight = this.animation ? `${this.body.offsetHeight}px` : '0px';
      const targetHeight = `${this.body.scrollHeight}px`;

      if (this.animation) {
        this.animation.cancel();
      }

      this.body.style.overflow = 'hidden';

      this.animation = this.body.animate(
        [
          {
            height: startHeight,
            opacity: startHeight === '0px' ? 0 : 0.4,
            transform: 'translateY(-6px)'
          },
          {
            height: targetHeight,
            opacity: 1,
            transform: 'translateY(0)'
          }
        ],
        {
          duration: DURATION_OPEN,
          easing: EASING_OPEN
        }
      );

      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => {
        this.isExpanding = false;
        this.el.classList.remove('is-animating');
      };
    }

    close() {
      if (this.isClosing) return;

      this.isExpanding = false;
      this.isClosing = true;

      this.el.classList.add('is-animating');
      this.el.classList.remove('is-expanded');
      this.summary.setAttribute('aria-expanded', 'false');

      if (!this.body) {
        this.el.open = false;
        this.isClosing = false;
        return;
      }

      const startHeight = `${this.body.offsetHeight}px`;
      const targetHeight = '0px';

      if (this.animation) {
        this.animation.cancel();
      }

      this.body.style.overflow = 'hidden';

      this.animation = this.body.animate(
        [
          {
            height: startHeight,
            opacity: 1,
            transform: 'translateY(0)'
          },
          {
            height: targetHeight,
            opacity: 0,
            transform: 'translateY(-6px)'
          }
        ],
        {
          duration: DURATION_CLOSE,
          easing: EASING_CLOSE
        }
      );

      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => {
        this.isClosing = false;
        this.el.classList.remove('is-animating');
      };
    }

    onAnimationFinish(isOpen) {
      this.el.open = isOpen;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.el.classList.remove('is-animating');

      if (this.body) {
        this.body.style.height = '';
        this.body.style.overflow = '';
      }
    }

    handleSingleOpenGroup() {
      const container = this.el.closest('[data-single-open="true"]');
      if (!container) return;

      const siblings = container.querySelectorAll('details.nt-accordion[open]');
      siblings.forEach((sibling) => {
        if (sibling !== this.el && sibling._ntAccordion) {
          sibling._ntAccordion.close();
        }
      });
    }
  }

  function initAccordions(root) {
    const scope = root || document;
    const accordions = scope.querySelectorAll('details.nt-accordion');
    accordions.forEach((el) => {
      if (!el._ntAccordion) {
        el._ntAccordion = new NTAccordionItem(el);
      }
    });
  }

  // Auto initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAccordions());
  } else {
    initAccordions();
  }

  // Support dynamic sections and Shopify Theme Editor events
  document.addEventListener('shopify:section:load', (e) => initAccordions(e.target));
  document.addEventListener('shopify:section:reorder', () => initAccordions());
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const accordion = block.matches('details.nt-accordion')
      ? block
      : block.querySelector('details.nt-accordion');
    if (accordion && accordion._ntAccordion && !accordion.open) {
      accordion._ntAccordion.open();
    }
  });

  // Export globally
  window.NTAccordion = {
    init: initAccordions,
    Item: NTAccordionItem
  };
})();
